import { Button } from '@components/common';
import { Pencil, RefreshCw } from 'lucide-react';
import React, { useMemo } from 'react';
import type {
  InventoryItemRecord,
  InventoryStockBalanceRecord,
  InventoryStockLocationRecord,
  InventoryUnitRecord,
} from '@/renderer/types/inventory';
import { formatReportMoney } from '@/renderer/util/reporting';

function groupBalancesByLocation(
  balances: InventoryStockBalanceRecord[],
  locations: InventoryStockLocationRecord[],
) {
  const groups = new Map<string, InventoryStockBalanceRecord[]>();
  balances.forEach((balance) => {
    const key = balance.stock_location_id;
    const existing = groups.get(key);
    if (existing) {
      existing.push(balance);
    } else {
      groups.set(key, [balance]);
    }
  });

  return Array.from(groups.entries())
    .map(([locationId, rows]) => ({
      locationId,
      locationName:
        locations.find((location) => location.id === locationId)?.name ||
        'Unknown location',
      rows,
      totalValue: rows.reduce((sum, row) => sum + row.total_value, 0),
    }))
    .sort((a, b) => a.locationName.localeCompare(b.locationName));
}

export function StockBalancesTable({
  balances,
  items,
  locations,
  units,
  isLoading = false,
  groupByLocation = false,
  onEdit,
}: {
  balances: InventoryStockBalanceRecord[];
  items: InventoryItemRecord[];
  locations: InventoryStockLocationRecord[];
  units: InventoryUnitRecord[];
  isLoading?: boolean;
  groupByLocation?: boolean;
  onEdit: (balance: InventoryStockBalanceRecord) => void;
}) {
  const locationGroups = useMemo(
    () => (groupByLocation ? groupBalancesByLocation(balances, locations) : []),
    [groupByLocation, balances, locations],
  );

  const renderRow = (balance: InventoryStockBalanceRecord) => {
    const item = items.find(
      (candidate) => candidate.id === balance.inventory_item_id,
    );
    const location = locations.find(
      (candidate) => candidate.id === balance.stock_location_id,
    );
    const unit = item
      ? units.find((u) => u.id === item.stock_unit_id)
      : undefined;
    const unitLabel = unit?.abbreviation || unit?.name || '';

    return (
      <tr key={balance.id} className="border-t border-gray-100">
        <td className="px-5 py-4">
          <div className="font-medium text-gray-900">
            {item?.name || 'Unknown item'}
          </div>
          <div className="text-xs text-gray-500">{item?.sku || 'No SKU'}</div>
        </td>
        <td className="px-5 py-4 text-gray-600">
          {location?.name || 'Unknown location'}
        </td>
        <td className="px-5 py-4 text-gray-600">
          {balance.quantity_on_hand.toLocaleString(undefined, {
            maximumFractionDigits: 4,
          })}
          {unitLabel && (
            <span className="ml-1 text-xs text-gray-400">{unitLabel}</span>
          )}
        </td>
        <td className="px-5 py-4 text-gray-600">
          {formatReportMoney(balance.average_cost)}
        </td>
        <td className="px-5 py-4 text-gray-600">
          {formatReportMoney(balance.total_value)}
        </td>
        <td className="px-5 py-4">
          <div className="flex justify-end gap-2">
            <Button variant="text" size="sm" onClick={() => onEdit(balance)}>
              <Pencil size={14} />
              Edit
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="relative max-h-[34rem] overflow-auto">
        {isLoading ? (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-start justify-center bg-white/65 pt-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 shadow-sm">
              <RefreshCw size={13} className="animate-spin" />
              Updating balances
            </span>
          </div>
        ) : null}
        <table
          className={`w-full text-sm transition-opacity ${isLoading ? 'opacity-60' : ''}`}
          aria-busy={isLoading}
        >
          <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Item
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Location
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Quantity
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Avg. cost
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Total value
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {groupByLocation
              ? locationGroups.flatMap((group) => [
                  <tr
                    key={`group-${group.locationId}`}
                    className="border-t border-gray-100 bg-gray-50"
                  >
                    <td
                      colSpan={4}
                      className="px-5 py-2 text-xs font-semibold uppercase tracking-wide text-gray-700"
                    >
                      {group.locationName}
                    </td>
                    <td
                      colSpan={2}
                      className="px-5 py-2 text-xs font-semibold text-gray-700"
                    >
                      {formatReportMoney(group.totalValue)}
                    </td>
                  </tr>,
                  ...group.rows.map((balance) => renderRow(balance)),
                ])
              : balances.map((balance) => renderRow(balance))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
